import { External } from '../../external';

const BASE_URL = `https://${External.serviceApiDomain}/comment/admin`;
const paginationLimit = 10;

const getPaginationQueryParams = (timestamp, limit, order) => {
  const searchParams = new URLSearchParams();

  if (timestamp) {
    searchParams.append('timestamp', timestamp);
  }
  if (limit) {
    searchParams.append('limit', limit);
  }
  if (order) {
    searchParams.append('order', order);
  }

  return searchParams.toString();
};

const WallsWidgetService = {
  getCurrentMember: async () => {
    return {
      memberId: -1 * External.userId,
      name: External.userName,
    };
  },
  isMember: async () => {
    return true;
  },
  isModerator: async () => {
    return true;
  },
  getContextStats: async ({ resourceType, resourceId }) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}/context-stats`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getPostsByResource: async ({ resourceType, resourceId }, timestamp, order, limit) => {
    const _limit = limit || paginationLimit;
    const url = `${BASE_URL}/${resourceType}/${resourceId}?${getPaginationQueryParams(timestamp, _limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getPostsByParent: async (parentId, timestamp, order, limit) => {
    const _limit = limit || paginationLimit;
    const url = `${BASE_URL}/${parentId}?${getPaginationQueryParams(timestamp, _limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  giveReaction: async (postId, reaction) => {
    const url = `${BASE_URL}/${postId}/react/${reaction}`;
    await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  removeReaction: async (postId) => {
    const url = `${BASE_URL}/${postId}/react`;
    await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  givePollVote: async (postId, optionId) => {
    const url = `${BASE_URL}/${postId}/poll/${optionId}`;
    await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  removePollVote: async (postId, optionId) => {
    const url = `${BASE_URL}/${postId}/poll/${optionId}`;
    await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  getCurrentAuthor: async () => {
    const currentMember = await WallsWidgetService.getCurrentMember();
    return {
      memberId: currentMember?.id,
      name: currentMember?.name || 'John Doe',
    };
  },
  isPostBelongToMember: async (post) => {
    if (!(await WallsWidgetService.isMember())) {
      return false;
    }

    const currentMember = await WallsWidgetService.getCurrentMember();
    const currentMemberId = currentMember?.id;
    return currentMemberId === post.author.memberId;
  },
  createPost: async ({ resourceType, resourceId }, encryptedWallConfig, content, attachments, settings) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}`;
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash,
        'X-Context-Cfg': encryptedWallConfig
      },
      body: JSON.stringify({
        content: content,
        attachments: attachments,
        settings: settings
      })
    });
    return await response.json();
  },
  updatePost: async (postId, content, attachments, settings) => {
    const url = `${BASE_URL}/${postId}`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        content: content,
        attachments: attachments,
        settings: settings
      })
    });

    return await response.json();
  },
  deletePost: async (postId) => {
    const url = `${BASE_URL}/${postId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  approvePost: async (postId) => {
    const url = `${BASE_URL}/${postId}/approve`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  uploadImageAndReturnDetails: async ({ resourceType, resourceId }, image) => {
    const CLIENT_UPLOADS_FOLDER = '__client_uploads__';
    return new Promise((resolve, reject) => {
      if (!image) {
        reject('Image is null!');
      }
      if (image.base) {
        reject('Already uploaded! image: ' + JSON.stringify(image));
      }
      Common.WebPlatform.makeClientWebsiteRequest({
        action: 'presignedUrl',
        data: {
          uploadingFileType: 'image',
          folder: CLIENT_UPLOADS_FOLDER,
          filename: `${resourceType}-${resourceId}-${image.name}`,
          fileType: image.type
        },
        onOk: async function (response) {
          if (response.ok) {
            if (response.data === 'NO_STORAGE_AVAILABLE') {
              reject('NO_STORAGE_AVAILABLE');
            } else {
              await fetch(response.data.url, {
                method: 'PUT',
                headers: {
                  'Content-Type': image.type
                },
                body: image
              });
              resolve({
                base: response.data.base,
                name: `${CLIENT_UPLOADS_FOLDER}/${response.data.fileName}`
              });
            }
          } else {
            reject('Response of presignedUrl was not ok: ' + response.message);
          }
        },
        onNotOk: function () {
          reject('Error while trying get presignedUrl: ' + response.message);
        }
      });
    });
  },
  getComment: async (commentId) => {
    const url = `${BASE_URL}/${commentId}/detail`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    if (!response.ok) {
      return null;
    }
    return await response.json();
  }
};

export default WallsWidgetService;
